import { useCallback, useState, type ReactNode } from "react";
import { Navigate, useParams } from "react-router";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import type { ObjectClassDefinition, ObjectFieldValuesDetails, PageLayout } from "../../../../api/types/objectTypes";
import useClientContext from "../../../../context/useClientContext";
import ObjectDetailsPage from "../../../entityFields/ObjectDetailsPage";
import type { FieldValuesSectionProps } from "../../../entityFields/useFieldValuesEditor";
import type { ObjectCardEditChecks } from "../../../entityFields/useObjectCardChecks";
import InvoiceAgent from "./InvoiceAgent";
import { withSource } from "./invoiceCardSource";
import InvoiceBanner from "./InvoiceBanner";
import InvoiceFieldValuesSection from "./InvoiceFieldValuesSection";
import useInvoiceCardPreviewConfig from "./useInvoiceCardPreviewConfig";
import useInvoiceSource from "./useInvoiceSource";

interface Props {
  backButtonPath: string;
  forApproval?: boolean;
}

const invoiceDeletingSignals: WorkflowSignalType[] = ["DeleteInvoice"];

const renderInvoiceAgent = (objectData: ObjectFieldValuesDetails): ReactNode => (
  <InvoiceAgent key={objectData.id} invoiceId={objectData.id} invoiceUpdateAt={objectData.updateAt} />
);

const InvoiceDetailsPage = ({ backButtonPath, forApproval }: Props) => {
  const { id = "" } = useParams();
  const { isPortalFeatureEnabled } = useClientContext();
  const cardPreview = useInvoiceCardPreviewConfig();
  const { source, isLoading, hasFailed, refresh } = useInvoiceSource(id);
  const [editChecks, setEditChecks] = useState<ObjectCardEditChecks>();

  const handleRefreshed = useCallback(() => setEditChecks(undefined), []);

  const adjustObjectDefinition = useCallback(
    (definition: ObjectClassDefinition) => (source ? withSource(definition, source) : definition),
    [source]
  );

  const renderBanner = (objectData: ObjectFieldValuesDetails, cardLayout: PageLayout) =>
    cardLayout.banner && <InvoiceBanner banner={cardLayout.banner} objectData={objectData} source={source} />;

  const renderFieldValues = (props: FieldValuesSectionProps) => (
    <InvoiceFieldValuesSection
      {...props}
      cardPreview={cardPreview}
      source={source}
      onEditChecksChange={setEditChecks}
    />
  );

  if (!id) {
    return <Navigate to=".." />;
  }

  if (hasFailed) {
    return <DataLoadingFailed />;
  }

  return (
    <ObjectDetailsPage
      forApproval={forApproval}
      objectId={id}
      objectType="Invoice"
      backButtonPath={backButtonPath}
      isLoading={isLoading}
      deletingSignals={invoiceDeletingSignals}
      checksFollowEdits={cardPreview !== undefined}
      editChecks={editChecks}
      adjustObjectDefinition={adjustObjectDefinition}
      onRefresh={refresh}
      onRefreshed={handleRefreshed}
      renderTopSection={
        isPortalFeatureEnabled("ExpenseManagementApprovalAgentEnabled") ? renderInvoiceAgent : undefined
      }
      renderBanner={renderBanner}
      renderFieldValues={renderFieldValues}
    />
  );
};

export default InvoiceDetailsPage;
